import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChartBarIcon, 
  TrophyIcon,
  ArrowLeftIcon,
  StopIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface VariantAnalytics {
  variantId: string;
  name: string;
  description: string;
  trafficAllocation: number;
  impressions: number;
  conversions: number;
  conversionRate: number;
  avgMetricValue: number;
}

interface Statistics {
  hasWinner: boolean;
  winningVariantId?: string;
  confidenceLevel: number;
  statisticalSignificance: boolean;
  pValue?: number;
  relativeLift?: number;
}

interface TestAnalytics {
  testId: string;
  name: string;
  description: string;
  testType: string;
  status: string;
  targetMetric: string;
  startDate: string;
  endDate: string;
  totalImpressions: number;
  totalConversions: number;
  overallConversionRate: number;
  variants: VariantAnalytics[];
  statistics: Statistics;
}

const ABTestAnalytics: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<TestAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (testId) {
      fetchAnalytics();
    }
  }, [testId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/ab-testing/${testId}/analytics`);
      setAnalytics(response.data);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const stopTest = async () => {
    if (!window.confirm('Are you sure you want to stop this test?')) {
      return;
    }

    try {
      await axios.post(`/api/ab-testing/${testId}/stop`);
      fetchAnalytics(); // Refresh
    } catch (err: any) {
      console.error('Error stopping test:', err);
      alert(err.response?.data?.error || 'Failed to stop test');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
      COMPLETED: { bg: 'bg-gray-100', text: 'text-gray-800', icon: CheckCircleIcon },
      SCHEDULED: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircleIcon },
    };
    const config = badges[status as keyof typeof badges] || badges.COMPLETED;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Test not found'}</p>
        </div>
      </div>
    );
  }

  const winner = analytics.variants.find(v => v.variantId === analytics.statistics.winningVariantId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard/ab-testing')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Tests
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{analytics.name}</h1>
              {getStatusBadge(analytics.status)}
            </div>
            <p className="text-gray-600">{analytics.description}</p>
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
              <span>Type: {analytics.testType}</span>
              <span>•</span>
              <span>Target: {analytics.targetMetric}</span>
              <span>•</span>
              <span>
                {new Date(analytics.startDate).toLocaleDateString()} -{' '}
                {new Date(analytics.endDate).toLocaleDateString()}
              </span>
            </div>
          </div>

          {analytics.status === 'ACTIVE' && (
            <button
              onClick={stopTest}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <StopIcon className="h-5 w-5" />
              Stop Test
            </button>
          )}
        </div>
      </div>

      {/* Winner Banner */}
      {analytics.statistics.hasWinner && winner && (
        <div className="mb-8 bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <TrophyIcon className="h-12 w-12 text-yellow-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Winner: {winner.name}
              </h2>
              <p className="text-gray-700">
                This variant achieved statistical significance with a{' '}
                <span className="font-semibold">
                  {analytics.statistics.relativeLift?.toFixed(2)}% lift
                </span>{' '}
                (p-value: {analytics.statistics.pValue?.toFixed(4)})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Impressions</p>
          <p className="text-3xl font-bold text-gray-900">{analytics.totalImpressions.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Conversions</p>
          <p className="text-3xl font-bold text-gray-900">{analytics.totalConversions.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Overall Conversion Rate</p>
          <p className="text-3xl font-bold text-gray-900">
            {(analytics.overallConversionRate * 100).toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Statistical Significance */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Statistical Analysis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Confidence Level</p>
            <p className="text-lg font-semibold text-gray-900">
              {(analytics.statistics.confidenceLevel * 100).toFixed(0)}%
            </p>
          </div>

          <div>
            <p className="text-sm text-gray-600 mb-1">Statistical Significance</p>
            <div className="flex items-center gap-2">
              {analytics.statistics.statisticalSignificance ? (
                <>
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  <span className="text-lg font-semibold text-green-600">Significant</span>
                </>
              ) : (
                <>
                  <XCircleIcon className="h-6 w-6 text-gray-400" />
                  <span className="text-lg font-semibold text-gray-600">Not Yet Significant</span>
                </>
              )}
            </div>
          </div>

          {analytics.statistics.pValue !== undefined && (
            <div>
              <p className="text-sm text-gray-600 mb-1">P-Value</p>
              <p className="text-lg font-semibold text-gray-900">
                {analytics.statistics.pValue.toFixed(4)}
              </p>
            </div>
          )}

          {analytics.statistics.relativeLift !== undefined && (
            <div>
              <p className="text-sm text-gray-600 mb-1">Relative Lift</p>
              <p className="text-lg font-semibold text-gray-900">
                {analytics.statistics.relativeLift > 0 ? '+' : ''}
                {analytics.statistics.relativeLift.toFixed(2)}%
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Variants Performance */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ChartBarIcon className="h-6 w-6 text-blue-600" />
            Variant Performance
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Traffic
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Impressions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversion Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Metric Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.variants.map((variant) => {
                const isWinner = variant.variantId === analytics.statistics.winningVariantId;
                
                return (
                  <tr 
                    key={variant.variantId} 
                    className={isWinner ? 'bg-yellow-50' : 'hover:bg-gray-50'}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {isWinner && <TrophyIcon className="h-5 w-5 text-yellow-600" />}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{variant.name}</div>
                          {variant.description && (
                            <div className="text-sm text-gray-500">{variant.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {variant.trafficAllocation}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {variant.impressions.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {variant.conversions.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        isWinner ? 'text-yellow-700' : 'text-gray-900'
                      }`}>
                        {(variant.conversionRate * 100).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {variant.avgMetricValue.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ABTestAnalytics;
